import React, { useState } from 'react';
import { MessageCircle, ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate } from '../utils/dateHelpers';

/**
 * Item de mensagem WhatsApp na timeline de uma oferta (ADR-022 Onda 2).
 *
 * Cores: verde WhatsApp (#25D366) para borda/avatar.
 * Direção: 'entrada' (recebido — verde claro) | 'saida' (enviado — verde forte).
 *
 * Rules of Hooks (Defensive Coding Regra 6): useState antes de qualquer return.
 */
export const WhatsAppTimelineItem = ({ message }) => {
  const [expanded, setExpanded] = useState(false);

  if (!message) return null;

  const isEntrada = message.direcao === 'entrada';
  const fullText = message.texto || '';
  const PREVIEW_LIMIT = 220;
  const longText = fullText.length > PREVIEW_LIMIT;
  const shownText = expanded || !longText ? fullText : `${fullText.slice(0, PREVIEW_LIMIT)}…`;

  // Verde WhatsApp para enviadas; verde claro para recebidas.
  const directionBorder = isEntrada
    ? 'border-l-4 border-l-emerald-300 bg-emerald-50/30'
    : 'border-l-4 border-l-[#25D366] bg-emerald-50/40';

  const avatarTone = isEntrada
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-[#25D366]/15 text-[#128C7E]';

  return (
    <div className={`flex gap-2 py-3 pl-2 border-b border-slate-100 last:border-0 group ${directionBorder}`}>
      <div className="flex-shrink-0 mt-1">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${avatarTone}`} title="WhatsApp">
          {isEntrada ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
        </div>
      </div>

      <div className="flex-grow min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <MessageCircle size={11} className="text-[#128C7E] flex-shrink-0" />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#128C7E]">
            WhatsApp · {isEntrada ? 'recebido' : 'enviado'}
          </span>
          {message.telefone_contato && (
            <span className="text-[11px] text-slate-600 truncate" title={message.telefone_contato}>
              {message.telefone_contato}
            </span>
          )}
          <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">
            {formatDate(message.data)}
          </span>
        </div>

        <div className="mt-1 text-[12px] text-slate-800 whitespace-pre-wrap break-words">
          {shownText}
        </div>

        {longText && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="mt-1 text-[11px] text-[#128C7E] hover:underline flex items-center gap-0.5"
          >
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {expanded ? 'menos' : 'mais'}
          </button>
        )}

        <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-400">
          {message.captured_by && (
            <span title={`Capturado em ${message.captured_at || '—'}`}>
              capturado por {message.captured_by}
            </span>
          )}
          {message.source_chat_label && (
            <span className="truncate" title={message.source_chat_label}>
              · {message.source_chat_label}
            </span>
          )}
          {message.parser_version && (
            <span>· parser {message.parser_version}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTimelineItem;
